import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, Table } from 'reactstrap';
import './poster-directory-modal.scss';
import {
    Box,
    Paper,
    SortDirection,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Toolbar,
    Typography,
} from '@mui/material';
import { Discussion } from '../models/discussion';
import { OnlineState } from '../models/online-state';
import { Member } from '../models/member';
import { Poster } from '../models/poster';
import usersGroupIcon from '../../assets/images/userGroups.png';
import { Link } from 'react-router-dom';
import { Practice } from '../models/practice';
import PocProfileModal from '../poc-profile-modal/poc-profile-modal';
import { USER_ROLES } from '../../utils/constants';
import { stateToCodeMapper } from '../../utils/locationsHelper';
import Helpers from '../../utils/helper';
import { DirectButton } from '../../direct/direct-button/direct-button';
import { useStateSelector } from '../../store/selectors';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PocAccessManagingToolsModal from '../../containers/Admin/Permissions/Users/Modal/PocAccessManagingToolsModal';
import createNotification from '../../utils/createNotification';
import { UserAccessRights } from '../models/userAccessRights';
import { GroupType } from '../models/group-type';
import {
    TimelineParty,
    TimelineParties,
} from '../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import Rodal from '../../components/Rodal/Rodal';

interface PosterDirectoryModalProps {
    isVisible: boolean;
    setVisibility(isVisible: boolean): void;
    discussion?: Discussion;
    discussions: Array<Discussion>;
    members: Array<Member>;
    groupType: GroupType;
    setExternalPopupId?(id: number): void;
    setExternalPopupVisibility?(isVisible: boolean): void;
}

interface PostersDirectoryMember {
    id: number;
    fullName: string;
    countOfPosts: number;
    graduationYear: number;
    veterinarySchool: string;
    foregroundColorCode: string;
    initials: string;
    backgroundColorCode: string;
    onlineState: OnlineState;
    practices: Array<Practice>;
    avatarPath: string;
    userId: number;
    isActive: boolean;
    marketplaceParty?: TimelineParty;
    marketplaceBuyerId?: number;
    isMarketplaceTeamMember?: boolean;
}

const PosterDirectoryModal = (props: PosterDirectoryModalProps) => {
    const axios = useStateSelector((state: any) => state.core.axios);

    const [order, setOrder] = useState('asc');
    const [search, setSearch] = useState('');
    const [orderBy, setOrderBy] = useState('fullName');
    const [selected, setSelected] = useState([]);

    const [loading, setLoading] = useState(false);

    const [isPocProfileModalVisible, setIsPocProfileModalVisible] =
        useState(false);
    const [isUserAccessRightsModalVisible, setIsUserAccessRightsModalVisible] =
        useState(false);

    const [isAdmin, setIsAdmin] = useState(false);

    const [selectedUserAccessRights, setSelectedUserAccessRights] =
        useState<UserAccessRights>(null);
    const [selectedMemberUserId, setSelectedMemberUserId] = useState(0);
    const [selectedMemberIdToShowInfoId, setSelectedMemberIdToShowInfoId] =
        useState(0);

    useEffect(() => {
        const summary = JSON.parse(localStorage.getItem('loggedInUserSummary'));
        const isAdmin = summary?.roleId === USER_ROLES.ADMIN;
        setIsAdmin(isAdmin);
    }, []);

    const toggleModalVisibility = () => {
        closeExternalModal();
        props.setVisibility(!props.isVisible);
        setSearch('');
    };

    const MapPosterDirectoryMember = (
        countOfPosts: number,
        member: Member
    ): PostersDirectoryMember => ({
        id: member.id,
        fullName: member.fullName,
        countOfPosts: countOfPosts,
        graduationYear: member.profile?.graduationYear,
        veterinarySchool: member.profile?.veterinarySchool,
        backgroundColorCode: member.backgroundColorCode,
        foregroundColorCode: member.foregroundColorCode,
        initials: member.initials,
        onlineState: member.onlineState,
        practices: member.profile?.practices,
        avatarPath: member.profile?.avatarPath,
        userId: member.userId,
        isActive: member.isActive && member.profile?.isActive,
        marketplaceParty: member.marketplaceParty,
        marketplaceBuyerId: member.marketplaceBuyerId,
        isMarketplaceTeamMember: member.isMarketplaceTeamMember,
    });
    let posters: Array<PostersDirectoryMember> = [];

    if (!props.discussion) {
        props.members
            ?.filter((f) => f.isActive)
            .forEach((member) => {
                let countOfPosts = 0;
                props.discussions.forEach((discussion) => {
                    let poster = discussion.posters.find(
                        (poster) => poster && poster.id === member.id
                    );

                    if (poster) {
                        countOfPosts += poster.countOfPosts;
                    }
                });
                posters.push(MapPosterDirectoryMember(countOfPosts, member));
            });
    } else {
        props.members
            ?.filter((f) => f.isActive)
            .forEach((member) => {
                props.discussion.posters.forEach((poster: Poster) => {
                    if (poster && poster.id === member.id) {
                        posters.push(
                            MapPosterDirectoryMember(
                                poster.countOfPosts,
                                member
                            )
                        );
                    }
                });
            });
    }

    const pocHeadCells = [
        {
            id: 'fullName',
            numeric: false,
            label: 'Name',
            sortable: true,
            isOnlyForAdmin: false,
            className: '',
        },
        {
            id: 'practices',
            numeric: false,
            label: 'Practice(s)',
            sortable: true,
            isOnlyForAdmin: false,
            className: '',
        },
        {
            id: 'graduationYear',
            numeric: false,
            label: 'Vet School, Year',
            sortable: true,
            isOnlyForAdmin: false,
            className: '',
        },
        {
            id: 'countOfPosts',
            numeric: true,
            label: '# of Posts',
            sortable: true,
            isOnlyForAdmin: false,
            className: '',
        },
        {
            id: 'action',
            numeric: false,
            label: 'Action',
            sortable: false,
            isOnlyForAdmin: true,
            className: '',
        },
        {
            id: 'userId',
            numeric: false,
            label: '',
            sortable: false,
            isOnlyForAdmin: false,
            className: '',
        },
    ];

    const MPHeadCells = [
        {
            id: 'fullName',
            numeric: false,
            label: 'Name',
            sortable: true,
            isOnlyForAdmin: false,
            className: 'mediumColumn',
        },
        {
            id: 'marketplaceParty',
            numeric: false,
            label: 'Role',
            sortable: true,
            isOnlyForAdmin: false,
            className: 'mediumColumn',
        },
        {
            id: 'affiliation',
            numeric: false,
            label: 'Affiliation',
            sortable: false,
            isOnlyForAdmin: false,
            className: '',
        },
    ];

    const handleSearch = (event: any) => {
        setSearch(event.target.value.toLowerCase());
    };
    const data = {
        directoryMembers: posters
            .filter(
                (item, index, array) =>
                    array.findIndex((f) => f.id === item.id) === index
            )
            .filter((poster) => poster.fullName.toLowerCase().includes(search)),
    };

    const editUserAccessRights = (directoryMember: PostersDirectoryMember) => {
        setSelectedMemberUserId(directoryMember.userId);
        getUserAccessRights(directoryMember.userId).then((response) => {
            setSelectedUserAccessRights(response.data);
            setLoading(false);
        });
        setIsUserAccessRightsModalVisible(true);
    };

    const getUserAccessRights = async (userId: number) => {
        setLoading(true);
        return await axios.get(`api/users/AccessRights`, {
            params: { userId: userId },
        });
    };

    const updateUserAccessRights = (userPocAccessRights: number) => {
        const body = {
            userPocAccessRights: userPocAccessRights,
            doesHaveAccessToKb: selectedUserAccessRights.doesHaveAccessToKb,
        };
        return axios
            .put(`/api/users/${selectedMemberUserId}/AccessRights`, body)
            .then(() => {
                createNotification(
                    'Access rights updated successfully',
                    'success'
                );
                onUserAccessRightsModalClose();
            })
            .catch(() => {
                createNotification('Something went wrong', 'error');
            });
    };

    const handleRequestSort = (property: any) => (_event: any) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const comparator = (left: any, right: any) => {
        if (left > right) return 1;
        if (left < right) return -1;
        return 0;
    };

    const stableSort = (posters: PostersDirectoryMember[]) => {
        const sortedArray = posters;
        sortedArray.sort((a, b) => {
            const propName = orderBy as keyof PostersDirectoryMember;

            if (propName === 'marketplaceParty') {
                return comparator(
                    TimelineParties.find((party) => party.id === a[propName])
                        ?.name,
                    TimelineParties.find((party) => party.id === b[propName])
                        ?.name
                );
            }

            if (propName === 'practices') {
                return comparator(a[propName][0].name, b[propName][0].name);
            }
            if (propName === 'graduationYear') {
                const compResult = comparator(
                    a.veterinarySchool,
                    b.veterinarySchool
                );
                if (!compResult) {
                    return comparator(a[propName], b[propName]);
                }

                return compResult;
            } else {
                return comparator(a[propName], b[propName]);
            }
        });

        const isAsc = order === 'asc';
        if (!isAsc) {
            sortedArray.reverse();
        }

        return sortedArray;
    };

    const getSelectedMember = () => {
        return props.members?.find(
            (m: Member) => m.id == selectedMemberIdToShowInfoId
        );
    };

    const currentMemberId = useStateSelector((state) => {
        return state.clubhouse?.clubhouse?.currentMemberId ?? 0;
    });

    const onUserAccessRightsModalClose = () => {
        setIsUserAccessRightsModalVisible(false);
        setSelectedUserAccessRights(null);
        setSelectedMemberUserId(0);
    };

    const closeExternalModal = () => {
        props.setExternalPopupVisibility(false);
        props.setExternalPopupId(0);
    };

    const openMemberPocProfileModal = (memberId: number) => {
        setSelectedMemberIdToShowInfoId(memberId);
        setIsPocProfileModalVisible(true);
    };

    const openExternalModal = (marketplaceBuyerId: number) => {
        props.setExternalPopupId(marketplaceBuyerId);
        props.setExternalPopupVisibility(true);
    };

    const PosterDirNamePrefix = () =>
        props.groupType == GroupType.MP
            ? ''
            : `${props.groupType == GroupType.IPOC ? 'IPOC' : 'PLC'} `;

    const getHeadCells = () => {
        const cells =
            props.groupType == GroupType.MP ? MPHeadCells : pocHeadCells;

        if (!isAdmin) {
            return cells.filter((hc) => !hc.isOnlyForAdmin);
        }
        return cells;
    };

    const getNameColumn = (
        directoryMember: PostersDirectoryMember,
        index: number
    ) => {
        const labelId = `enhanced-table-checkbox-${index}`;

        if (props.groupType == GroupType.MP) {
            return (
                <TableCell id={labelId} padding="none">
                    <span className="poster-member-name">
                        {directoryMember.fullName}
                    </span>

                    {directoryMember.onlineState == OnlineState.Offline && (
                        <span className="onlineDot" />
                    )}
                    {directoryMember.onlineState == OnlineState.Online && (
                        <span className="offlineDot" />
                    )}
                </TableCell>
            );
        }

        return (
            <TableCell id={labelId} padding="none">
                {directoryMember.avatarPath ? (
                    <div
                        onClick={(e) => {
                            openMemberPocProfileModal(directoryMember.id);
                            e.stopPropagation();
                        }}
                        className="table-avatar-icon">
                        <span className="clientLogin">
                            <img
                                src={directoryMember.avatarPath}
                                className="poster-member-icon"
                            />
                        </span>
                    </div>
                ) : (
                    <div
                        onClick={(e) => {
                            openMemberPocProfileModal(directoryMember.id);
                            e.stopPropagation();
                        }}
                        className="poster-member-icon"
                        style={{
                            background: directoryMember.backgroundColorCode,
                        }}>
                        <div
                            className="poster-member-initials"
                            style={{
                                color: directoryMember.foregroundColorCode,
                            }}>
                            {directoryMember.initials}
                        </div>
                    </div>
                )}

                <a
                    onClick={(e) => {
                        openMemberPocProfileModal(directoryMember.id);
                        e.stopPropagation();
                    }}>
                    {directoryMember.fullName}
                </a>

                {directoryMember.onlineState == OnlineState.Offline && (
                    <span className="onlineDot" />
                )}
                {directoryMember.onlineState == OnlineState.Online && (
                    <span className="offlineDot" />
                )}
            </TableCell>
        );
    };

    const getPracticesColumnContent = (
        directoryMember: PostersDirectoryMember
    ) => {
        return directoryMember.isActive
            ? directoryMember.practices?.map((practice: Practice, index) => (
                  <span key={index}>
                      {practice.website ? (
                          <Link target={'_blank'} to={'//' + practice.website}>
                              {practice.name}
                          </Link>
                      ) : (
                          practice.name
                      )}{' '}
                      - {practice.city}, {stateToCodeMapper(practice.state)}{' '}
                      <br />
                  </span>
              )) ?? '-'
            : '-';
    };

    const getAffiliationColumnContent = (
        directoryMember: PostersDirectoryMember
    ) => {
        if (
            directoryMember.marketplaceParty == TimelineParty.Seller ||
            directoryMember.marketplaceParty == TimelineParty.Broker
        ) {
            return getPracticesColumnContent(directoryMember);
        }

        if (directoryMember.marketplaceParty == TimelineParty.Buyer) {
            const name = directoryMember.isMarketplaceTeamMember
                ? data.directoryMembers.find(
                      (dm) =>
                          dm.marketplaceBuyerId ===
                              directoryMember.marketplaceBuyerId &&
                          !dm.isMarketplaceTeamMember
                  )?.fullName
                : directoryMember.fullName;
            return (
                <>
                    <a
                        onClick={(e) => {
                            openExternalModal(
                                directoryMember.marketplaceBuyerId ?? 0
                            );
                            e.stopPropagation();
                        }}>
                        {name}
                    </a>
                </>
            );
        }

        return '-';
    };

    const getMPRowCells = (
        directoryMember: PostersDirectoryMember,
        index: number
    ) => {
        return (
            <TableRow
                hover
                role="checkbox"
                tabIndex={-1}
                key={directoryMember.fullName}>
                {getNameColumn(directoryMember, index)}

                <TableCell align="right">
                    {TimelineParties.find(
                        (party) => party.id === directoryMember.marketplaceParty
                    )?.name ?? '-'}
                    {directoryMember.isMarketplaceTeamMember
                        ? ' (Team Member)'
                        : ''}
                </TableCell>

                <TableCell align="right">
                    {getAffiliationColumnContent(directoryMember)}
                </TableCell>
            </TableRow>
        );
    };

    const getPocRowCells = (
        directoryMember: PostersDirectoryMember,
        index: number
    ) => {
        return (
            <TableRow
                hover
                role="checkbox"
                tabIndex={-1}
                key={directoryMember.fullName}>
                {getNameColumn(directoryMember, index)}

                <TableCell align="right">
                    {directoryMember.isActive
                        ? directoryMember.practices?.map(
                              (practice: Practice, index) => (
                                  <span key={index}>
                                      {practice.website ? (
                                          <Link
                                              target={'_blank'}
                                              to={'//' + practice.website}>
                                              {practice.name}
                                          </Link>
                                      ) : (
                                          practice.name
                                      )}{' '}
                                      - {practice.city},{' '}
                                      {stateToCodeMapper(practice.state)} <br />
                                  </span>
                              )
                          )
                        : '-'}
                </TableCell>
                <TableCell align="right">
                    {`${directoryMember.veterinarySchool ?? '-'}, ${
                        directoryMember.graduationYear?.toString() ?? '-'
                    }`}
                </TableCell>
                <TableCell align="center">
                    {directoryMember.countOfPosts}
                </TableCell>
                {isAdmin ? (
                    <TableCell align="center">
                        <span
                            title="Access Rights"
                            className="icon-hover-style"
                            style={{
                                marginLeft: '10px',
                                marginRight: '15px',
                            }}
                            onClick={() =>
                                editUserAccessRights(directoryMember)
                            }>
                            <FontAwesomeIcon size="lg" icon={faEye} />
                        </span>
                    </TableCell>
                ) : (
                    []
                )}
                <TableCell align="center">
                    {directoryMember.isActive ? (
                        <DirectButton
                            size="small"
                            clickCallback={() => {
                                closeExternalModal();
                                props.setVisibility(false);
                            }}
                            memberId={directoryMember.userId}
                        />
                    ) : (
                        []
                    )}
                </TableCell>
            </TableRow>
        );
    };

    const getRowCells = (
        directoryMember: PostersDirectoryMember,
        index: number
    ) => {
        return props.groupType == GroupType.MP
            ? getMPRowCells(directoryMember, index)
            : getPocRowCells(directoryMember, index);
    };

    return (
        <div className="posterDirectoryModal">
            <Rodal
                animation={'fade'}
                showMask={true}
                centered
                visible={props.isVisible}
                onClose={() => {
                    closeExternalModal();
                    props.setVisibility(false);
                    setSearch('');
                }}>
                <div className="create-new-discussion-popup-header">
                    <ModalHeader>
                        Poster Directory -{' '}
                        {props.discussion
                            ? `${props.discussion.name}`
                            : `${PosterDirNamePrefix()}Members`}
                    </ModalHeader>
                </div>
                <ModalBody className="editTitleBody deleteModalBody profileViewPopUpBody">
                    <div className="serachBar">
                        <img src={usersGroupIcon} />
                        <input
                            type="text"
                            placeholder="Search Directory"
                            onChange={handleSearch}
                        />
                    </div>
                    <Box sx={{ width: '100%' }}>
                        <Paper>
                            <Toolbar>
                                {selected.length > 0 ? (
                                    <Typography
                                        color="inherit"
                                        variant="subtitle1"
                                        component="div">
                                        {selected.length} selected
                                    </Typography>
                                ) : (
                                    <Typography
                                        variant="h6"
                                        id="tableTitle"
                                        component="div">
                                        Nutrition
                                    </Typography>
                                )}
                            </Toolbar>
                            <TableContainer>
                                <Table
                                    className="posterDirectorTable"
                                    sx={{ minWidth: 450 }}
                                    aria-labelledby="tableTitle">
                                    <TableHead>
                                        <TableRow>
                                            {getHeadCells().map((headCell) => (
                                                <TableCell
                                                    className={
                                                        headCell.className
                                                    }
                                                    key={headCell.id}
                                                    align={
                                                        headCell.numeric
                                                            ? 'center'
                                                            : 'left'
                                                    }
                                                    sortDirection={
                                                        orderBy === headCell.id
                                                            ? (order as SortDirection)
                                                            : false
                                                    }>
                                                    {headCell.sortable ? (
                                                        <TableSortLabel
                                                            active={
                                                                orderBy ===
                                                                headCell.id
                                                            }
                                                            direction={
                                                                orderBy ===
                                                                headCell.id
                                                                    ? (order as
                                                                          | 'asc'
                                                                          | 'desc')
                                                                    : 'asc'
                                                            }
                                                            onClick={handleRequestSort(
                                                                headCell.id
                                                            )}>
                                                            {headCell.label}
                                                            {orderBy ===
                                                            headCell.id ? (
                                                                <Box component="span">
                                                                    {order ===
                                                                    'desc'
                                                                        ? ''
                                                                        : ''}
                                                                </Box>
                                                            ) : null}
                                                        </TableSortLabel>
                                                    ) : (
                                                        <span className="MuiButtonBase-root MuiTableSortLabel-root css-1qgma8u-MuiButtonBase-root-MuiTableSortLabel-root">
                                                            {headCell.label}
                                                        </span>
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {(!data.directoryMembers ||
                                            data.directoryMembers.length ===
                                                0) && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={12}
                                                    align="center">
                                                    No Data Available
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {data.directoryMembers &&
                                            stableSort(
                                                data.directoryMembers
                                            ).map((directoryMember, index) =>
                                                getRowCells(
                                                    directoryMember,
                                                    index
                                                )
                                            )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Box>
                </ModalBody>
                {isUserAccessRightsModalVisible && !loading ? (
                    <PocAccessManagingToolsModal
                        userId={selectedMemberUserId}
                        userAccessRights={selectedUserAccessRights}
                        updateUserPocAccessRights={updateUserAccessRights}
                        onClose={onUserAccessRightsModalClose}
                    />
                ) : (
                    []
                )}
            </Rodal>
            {getSelectedMember() &&
            (currentMemberId || Helpers.hasAccessRight(USER_ROLES.ADMIN)) &&
            isPocProfileModalVisible ? (
                <PocProfileModal
                    isVisible={isPocProfileModalVisible}
                    setVisibility={(isVisible, closePopup) => {
                        setIsPocProfileModalVisible(isVisible);
                        setSelectedMemberIdToShowInfoId(0);
                        if (closePopup) {
                            closeExternalModal();
                            props.setVisibility(false);
                        }
                    }}
                    currentMemberId={currentMemberId}
                    member={getSelectedMember()}
                />
            ) : (
                []
            )}
        </div>
    );
};

export default PosterDirectoryModal;
